import React, { Component, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import Friends from '@/components/Friends';
import styles from './Dashboard.module.scss';
import { apiService } from '@/services/api';

const DEFAULT_AVATAR = 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y&s=200';

interface DashboardState {
  displayName: string;
  profilePic: string;
  showPostsMenu: boolean;
}

// Higher-order component to provide navigation
function withNavigation(WrappedComponent: any) {
  return function WithNavigationComponent(props: any) {
    const navigate = useNavigate();
    return <WrappedComponent {...props} navigate={navigate} />;
  };
}

class Dashboard extends Component<any, DashboardState> {
  constructor(props: any) {
    super(props);
    this.state = {
      displayName: '',
      profilePic: DEFAULT_AVATAR,
      showPostsMenu: false
    };
  }

  checkProfileCompletion = async () => {
    try {
      const profileResponse = await apiService.getCustomerProfile();
      if (profileResponse.success && profileResponse.data) {
        const profile = profileResponse.data;
        const isProfileComplete = profile.isProfileComplete ?? false;

        const name = profile.displayName ||
          (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : null) ||
          profile.firstName ||
          profile.email?.split('@')[0] ||
          'Player';
        const pic = profile.profilePic || profile.profile_pic || DEFAULT_AVATAR;
        this.setState({ displayName: name, profilePic: pic });

        if (!isProfileComplete) {
          console.log('Profile incomplete, redirecting to profile page');
          this.props.navigate('/profile');
          return;
        }
      } else {
        console.log('Profile fetch failed or returned no data, redirecting to profile page');
        this.props.navigate('/profile');
        return;
      }
    } catch (error: any) {
      console.log('Profile check failed, redirecting to profile page:', error.message);
      this.props.navigate('/profile');
      return;
    }
  };

  async componentDidMount() {
    // Check profile completion first
    await this.checkProfileCompletion();
  }

  render() {
    return (
      <>
        <DashboardHeader />
        <div className={styles.dashboardContainer}>
          <div className={styles.dashboardContent}>
            {/* Welcome Section */}
            <div className={styles.welcomeSection}>
              <div className={styles.userInfo}>
                <div className={styles.userAvatar}>
                  <img
                    src={this.state.profilePic}
                    alt={this.state.displayName || 'Profile'}
                    className={styles.avatarImage}
                    onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_AVATAR; }}
                  />
                </div>
                <div className={styles.userDetails}>
                  <h1 className={styles.welcomeTitle}>
                    Welcome{this.state.displayName ? `, ${this.state.displayName}` : ''}!
                  </h1>
                  {
                    /*
                    <div className={styles.userStats}>
                      <span className={styles.stat}>Rank: Gold</span>
                      <span className={styles.stat}>Wins: 24</span>
                      <span className={styles.stat}>Total Games: 45</span>
                    </div>
                    */
                  }
                </div>
              </div>
            </div>

            {/* Social Posts — one compact menu that opens the two options on tap,
                so this screen (reached from "Connections") isn't dominated by big boxes. */}
            <div className={styles.postsMenu}>
              <button
                type="button"
                className={styles.postsMenuTrigger}
                aria-expanded={this.state.showPostsMenu}
                onClick={() => this.setState((s) => ({ showPostsMenu: !s.showPostsMenu }))}
              >
                <span className={styles.postsMenuIcon}>📝</span>
                <span className={styles.postsMenuLabel}>Social Posts</span>
                <svg
                  className={`${styles.postsChevron} ${this.state.showPostsMenu ? styles.postsChevronOpen : ''}`}
                  width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {this.state.showPostsMenu && (
                <div className={styles.postsMenuOptions}>
                  <button
                    type="button"
                    className={styles.postsMenuOption}
                    onClick={() => this.props.navigate('/customer/social/feed')}
                  >
                    <span className={styles.optIcon}>📱</span>
                    <span className={styles.optText}>
                      <strong>View Posts</strong>
                      <em>Discover posts from other people</em>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={styles.postsMenuOption}
                    onClick={() => this.props.navigate('/customer/social/posts')}
                  >
                    <span className={styles.optIcon}>✍️</span>
                    <span className={styles.optText}>
                      <strong>My Posts</strong>
                      <em>Create and manage your posts</em>
                    </span>
                  </button>
                </div>
              )}
            </div>

            {/* Friends & Groups Content - Directly displayed without tabs */}
            <div className={styles.friendsTabContent}>
              <Suspense fallback={<div style={{color: 'var(--text-primary)', padding: '2rem', textAlign: 'center'}}>Loading Friends...</div>}>
                <Friends />
              </Suspense>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
}

export default withNavigation(Dashboard);
